import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconCheck,
  IconLoader2,
  IconPencil,
  IconPlus,
  IconPower,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import {
  createExpenseType,
  fetchExpenseTypes,
  updateExpenseType,
} from '../services/reembolsosService';
import { GrHoverTooltip } from './GestaoReembolsosTooltip';
import './GestaoExpenseTypesModal.css';

const TABS = [
  { key: 'ativas', label: 'Ativas' },
  { key: 'inativas', label: 'Inativas' },
  { key: 'todas', label: 'Todas' },
];

function norm(s) {
  return String(s || '')
    .trim()
    .toLowerCase();
}

/**
 * Gestão de categorias (LCMY_GO_EXPENSE_TYPE) — layout moderno (chips / tabs / cadastro rápido).
 */
export default function GestaoExpenseTypesModal({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ hasActive: false, hasDescription: false });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('ativas');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [shake, setShake] = useState(0);
  const [justAdded, setJustAdded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchExpenseTypes('');
      setItems(data?.items || []);
      setMeta(data?.meta || { hasActive: false, hasDescription: false });
    } catch (e) {
      setError(e?.message || 'Erro ao carregar categorias');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setNewName('');
    setQuery('');
    setFilter('ativas');
    setEditingId(null);
    setDraft('');
    setConfirmId(null);
    setError('');
    setInputFocused(false);
    setJustAdded(null);
    load();
    return undefined;
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) {
        if (confirmId != null) {
          setConfirmId(null);
          return;
        }
        if (editingId != null) {
          setEditingId(null);
          setDraft('');
          return;
        }
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, confirmId, editingId, onClose]);

  const counts = useMemo(() => {
    const actives = items.filter((i) => i.active !== false).length;
    return {
      ativas: actives,
      inativas: items.length - actives,
      todas: items.length,
    };
  }, [items]);

  const visible = useMemo(() => {
    const q = norm(query);
    return items
      .filter((i) => {
        if (filter === 'ativas') return i.active !== false;
        if (filter === 'inativas') return i.active === false;
        return true;
      })
      .filter((i) => !q || norm(i.name).includes(q))
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
  }, [items, filter, query]);

  const hasDraft = Boolean(newName.trim());

  const flashAdded = (id) => {
    setJustAdded(id);
    window.setTimeout(() => setJustAdded((cur) => (String(cur) === String(id) ? null : cur)), 700);
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name || saving) return;

    const dup = items.find((i) => norm(i.name) === norm(name));
    if (dup) {
      setShake((k) => k + 1);
      setError(
        dup.active === false
          ? `“${dup.name}” existe como inativa — ative na aba Inativas.`
          : `“${dup.name}” já está cadastrada.`
      );
      return;
    }

    setSaving(true);
    setError('');
    try {
      const created = await createExpenseType({ name, active: true });
      setNewName('');
      if (filter === 'inativas') setFilter('ativas');
      setQuery('');
      await load();
      if (created?.id != null) flashAdded(created.id);
    } catch (e) {
      setShake((k) => k + 1);
      setError(e?.message || 'Erro ao cadastrar');
    } finally {
      setSaving(false);
    }
  };

  const commitEdit = async () => {
    if (editingId == null || saving) return;
    const name = draft.trim();
    if (!name) {
      setEditingId(null);
      setDraft('');
      return;
    }
    const dup = items.find(
      (i) => String(i.id) !== String(editingId) && norm(i.name) === norm(name)
    );
    if (dup) {
      setError('Já existe uma categoria com esse nome.');
      setEditingId(null);
      setDraft('');
      return;
    }
    const current = items.find((i) => String(i.id) === String(editingId));
    if (current && norm(current.name) === norm(name)) {
      setEditingId(null);
      setDraft('');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await updateExpenseType(editingId, { name });
      setEditingId(null);
      setDraft('');
      await load();
    } catch (e) {
      setError(e?.message || 'Erro ao renomear');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    if (!meta.hasActive || saving) return;
    setSaving(true);
    setError('');
    setConfirmId(null);
    setEditingId(null);
    try {
      await updateExpenseType(row.id, { active: row.active === false });
      await load();
    } catch (e) {
      setError(e?.message || 'Erro ao alterar status');
    } finally {
      setSaving(false);
    }
  };

  /** Soft-delete: inativa (sem DELETE físico — lançamentos podem referenciar a categoria) */
  const softDelete = async (row) => {
    if (saving) return;
    if (!meta.hasActive) {
      setConfirmId(null);
      setError('Exclusão indisponível: a tabela não expõe coluna de ativo.');
      return;
    }
    if (row.active === false) {
      setConfirmId(null);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateExpenseType(row.id, { active: false });
      setConfirmId(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Erro ao excluir');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const emptyTitle = query.trim()
    ? 'Nenhuma categoria encontrada'
    : filter === 'inativas'
      ? 'Nenhuma categoria inativa'
      : 'Nenhuma categoria cadastrada';
  const emptyHint = query.trim()
    ? `Ajuste o filtro ou cadastre “${query.trim()}”.`
    : 'Digite um nome no campo acima e pressione Enter.';

  return createPortal(
    <div
      className="modal-overlay gr-cats-overlay"
      role="presentation"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className="gr-cats-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gr-cats-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="gr-cats-head">
          <div className="gr-cats-head-text">
            <h2 id="gr-cats-title" className="gr-cats-title">
              Gestão de categorias
            </h2>
            <p className="gr-cats-sub">
              Categorias usadas nos reembolsos. Digite um nome e pressione{' '}
              <kbd className="gr-cats-kbd">Enter</kbd> para cadastrar.
            </p>
          </div>
          <button
            type="button"
            className="gr-cats-icon-btn"
            onClick={() => !saving && onClose?.()}
            aria-label="Fechar"
          >
            <IconX size={16} stroke={1.6} />
          </button>
        </header>

        <div className="gr-cats-body">
          <div
            className={[
              'gr-cats-composer',
              inputFocused ? 'is-focused' : '',
              error ? 'is-error' : '',
              error && shake ? 'is-shake' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            key={shake}
          >
            <span className="gr-cats-composer-plus" aria-hidden>
              <IconPlus size={12} stroke={2.2} />
            </span>
            <input
              type="text"
              className="gr-cats-composer-input"
              value={newName}
              maxLength={200}
              placeholder="Nova categoria — ex. Alimentação"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={saving}
              onChange={(e) => {
                setNewName(e.target.value);
                if (error) setError('');
              }}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
                  if (newName.trim()) {
                    e.preventDefault();
                    handleAdd();
                  }
                }
                if (e.key === 'Escape') {
                  setNewName('');
                  setError('');
                }
              }}
            />
            {hasDraft ? (
              <button
                type="button"
                className="gr-cats-cadastrar"
                onClick={handleAdd}
                disabled={saving}
              >
                {saving ? '…' : 'Cadastrar'}
                <span className="gr-cats-enter-hint" aria-hidden>
                  ↵
                </span>
              </button>
            ) : null}
          </div>
          <div className="gr-cats-error-slot" role={error ? 'alert' : undefined}>
            {error || '\u00a0'}
          </div>

          <div className="gr-cats-toolbar">
            <div className="gr-cats-tabs" role="tablist" aria-label="Filtrar por status">
              {TABS.map((t) => {
                const on = filter === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    className={`gr-cats-tab${on ? ' is-active' : ''}`}
                    onClick={() => {
                      setFilter(t.key);
                      setConfirmId(null);
                      setEditingId(null);
                    }}
                  >
                    {t.label}
                    <span className="gr-cats-tab-count">{counts[t.key]}</span>
                  </button>
                );
              })}
            </div>
            <label className="gr-cats-filter">
              <IconSearch size={14} stroke={1.6} aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrar…"
                disabled={saving}
              />
            </label>
          </div>

          <div className="gr-cats-chips-wrap">
            {loading ? (
              <div className="gr-cats-state">
                <IconLoader2 size={18} className="pcs2-spin" aria-hidden />
                Carregando categorias…
              </div>
            ) : !visible.length ? (
              <div className="gr-cats-state is-empty">
                <div className="gr-cats-empty-title">{emptyTitle}</div>
                <div className="gr-cats-empty-hint">{emptyHint}</div>
              </div>
            ) : (
              <div className="gr-cats-chips" role="list">
                {visible.map((row) => {
                  const id = String(row.id);
                  const isEditing = editingId != null && String(editingId) === id;
                  const isConfirming = confirmId != null && String(confirmId) === id;
                  const isInactive = row.active === false;
                  const isNew = justAdded != null && String(justAdded) === id;

                  return (
                    <div
                      key={id}
                      role="listitem"
                      className={[
                        'gr-cats-chip',
                        isInactive ? 'is-inactive' : '',
                        isConfirming ? 'is-confirm' : '',
                        isEditing ? 'is-editing' : '',
                        isNew ? 'is-pop' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            className="gr-cats-chip-edit-input"
                            value={draft}
                            maxLength={200}
                            disabled={saving}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => commitEdit()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                commitEdit();
                              }
                              if (e.key === 'Escape') {
                                setEditingId(null);
                                setDraft('');
                              }
                            }}
                          />
                          <GrHoverTooltip content="Salvar" delayMs={200}>
                            <button
                              type="button"
                              className="gr-cats-chip-save"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={commitEdit}
                              aria-label="Salvar nome"
                              disabled={saving}
                            >
                              <IconCheck size={12} stroke={2} />
                            </button>
                          </GrHoverTooltip>
                        </>
                      ) : isConfirming ? (
                        <>
                          <span className="gr-cats-confirm-text">
                            Excluir “{row.name}”?
                          </span>
                          <button
                            type="button"
                            className="gr-cats-confirm-yes"
                            onClick={() => softDelete(row)}
                            disabled={saving}
                          >
                            Inativar
                          </button>
                          <button
                            type="button"
                            className="gr-cats-confirm-no"
                            onClick={() => setConfirmId(null)}
                            disabled={saving}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <GrHoverTooltip content="Clique para renomear" delayMs={280}>
                            <button
                              type="button"
                              className="gr-cats-chip-name"
                              onClick={() => {
                                setEditingId(row.id);
                                setDraft(row.name || '');
                                setConfirmId(null);
                                setError('');
                              }}
                            >
                              {row.name || '—'}
                            </button>
                          </GrHoverTooltip>
                          {isInactive ? (
                            <span className="gr-cats-inactive-pill">Inativa</span>
                          ) : null}
                          <span className="gr-cats-chip-actions">
                            <GrHoverTooltip content="Renomear" delayMs={200}>
                              <button
                                type="button"
                                className="gr-cats-chip-btn"
                                aria-label={`Renomear ${row.name || 'categoria'}`}
                                disabled={saving}
                                onClick={() => {
                                  setEditingId(row.id);
                                  setDraft(row.name || '');
                                  setConfirmId(null);
                                  setError('');
                                }}
                              >
                                <IconPencil size={13} stroke={1.5} />
                              </button>
                            </GrHoverTooltip>
                            {meta.hasActive ? (
                              <GrHoverTooltip
                                content={isInactive ? 'Ativar' : 'Inativar'}
                                delayMs={200}
                              >
                                <button
                                  type="button"
                                  className={`gr-cats-chip-btn${isInactive ? ' is-power-off' : ''}`}
                                  aria-label={
                                    isInactive
                                      ? `Ativar ${row.name || 'categoria'}`
                                      : `Inativar ${row.name || 'categoria'}`
                                  }
                                  disabled={saving}
                                  onClick={() => toggleActive(row)}
                                >
                                  <IconPower size={13} stroke={1.5} />
                                </button>
                              </GrHoverTooltip>
                            ) : null}
                            {meta.hasActive && !isInactive ? (
                              <GrHoverTooltip content="Excluir (inativar)" delayMs={200}>
                                <button
                                  type="button"
                                  className="gr-cats-chip-btn is-danger"
                                  aria-label={`Excluir ${row.name || 'categoria'}`}
                                  disabled={saving}
                                  onClick={() => {
                                    setConfirmId(row.id);
                                    setEditingId(null);
                                  }}
                                >
                                  <IconTrash size={13} stroke={1.5} />
                                </button>
                              </GrHoverTooltip>
                            ) : null}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
